import React from 'react';
import { Card, CardHeader, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function QueryCard({ query }) {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Resolved':
        return <CheckCircle className="h-4 w-4" />;
      case 'In Progress':
        return <Clock className="h-4 w-4" />;
      case 'Pending':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card 
      className="hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200"
      onClick={() => navigate(`/query/${query.id}`, { state: { query } })}
    >
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <Badge className={getStatusColor(query.status)}>
                <span className="flex items-center space-x-1">
                  {getStatusIcon(query.status)}
                  <span>{query.status}</span>
                </span>
              </Badge>
              <Badge variant="outline">{query.queryType}</Badge>
              {query.priority === 'Priority' && (
                <Badge className="bg-red-100 text-red-800">Priority</Badge>
              )}
            </div>
            <h3 className="text-lg font-semibold text-[#003366]">
              Query #{String(query.id).slice(0, 8).toUpperCase()}
            </h3>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <p className="text-gray-700 mb-4 line-clamp-2">
          {query.description}
        </p>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Submitted</p>
            <p className="font-medium text-gray-900">{formatDate(query.submittedAt)}</p>
          </div>
          <div>
            <p className="text-gray-500">Response Time</p>
            <p className="font-medium text-gray-900">{query.responseTime}</p>
          </div>
        </div>

        {query.status === 'Resolved' && query.resolvedAt && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-green-800 font-medium">
              Resolved on {formatDate(query.resolvedAt)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
